import { SANCTIONED_ISO_COUNTRY_CODES_SET } from 'omakase/FormControls/FormIsoCountryCodeField';
import { SelectFieldProps } from 'ui/SelectField';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import FormSelectField from 'ui/FormSelectField';
import IsoCountryCodes from 'constants/IsoCountryCodes';

interface Props extends Pick<SelectFieldProps, Exclude<keyof SelectFieldProps, 'children'>> {
  children?: React.ReactNode;
  /**
   * Disallow selection of sanctioned ISO country codes. They will appear in the select but be disabled.
   * Whether this prop has any effect or not is also controlled by the
   * DISALLOW_FRONTEND_SANCTIONED_PHONE_COUNTRY_CODE_SELECTION feature flag; both the prop and the
   * flag are needed for a behavior change.
   */
  disallowSanctionedCodes?: boolean;
}

const FormIsoCountryCodeField: React.FunctionComponent<Props> = ({
  name = 'isoCountryCode',
  id = 'isoCountryCode',
  label = 'Country',
  SelectProps = {
    inputProps: {
      'data-testid': 'country-code',
      autoComplete: 'country',
    },
  },
  fullWidth = true,
  disallowSanctionedCodes = false,
  ...rest
}) => {
  const canDisallowSanctionedCodes = useFeatureFlagForConsumer(
    'DISALLOW_FRONTEND_SANCTIONED_PHONE_COUNTRY_CODE_SELECTION'
  );
  disallowSanctionedCodes = disallowSanctionedCodes && canDisallowSanctionedCodes;

  return (
    <FormSelectField
      fullWidth={fullWidth}
      name={name}
      id={id}
      label={label}
      SelectProps={SelectProps}
      {...rest}
    >
      <option value="" />
      {IsoCountryCodes.map(({ value, key, label }) => (
        <option
          value={value}
          key={key}
          disabled={disallowSanctionedCodes && SANCTIONED_ISO_COUNTRY_CODES_SET.has(value)}
        >
          {label}
        </option>
      ))}
    </FormSelectField>
  );
};

export default FormIsoCountryCodeField;
